// Retail price calculation helpers — shared between the scraper edge
// function and the admin manual-entry form so both normalize prices to
// USD/kg exactly the same way.
//
// Mirrors supabase/functions/_shared/retail-scrape-processor.ts
// (convertToKg / computeUsdPerKg / fetchFxRates). Keep these in sync.

const UNIT_TO_KG: Record<string, number> = {
  kg: 1,
  g: 0.001,
  gram: 0.001,
  grams: 0.001,
  lb: 0.453592,
  lbs: 0.453592,
  oz: 0.0283495,
};

const COUNT_UNITS = new Set([
  'pack',
  'pk',
  'pcs',
  'pc',
  'stuks',
  'each',
  'pieza',
  'unidad',
  'unit',
]);

// Average weight per piece, by species_id. Only used when a SKU is
// sold by count (pcs/pieza) and we still want a USD/kg estimate.
const PER_PIECE_KG_BY_SPECIES: Record<number, number> = {
  8: 0.08, // kiwi (~80g)
  2: 0.008, // cherry (~8g)
};

/** Selectable units for the manual entry form, grouped for the UI. */
export const RETAIL_UNIT_OPTIONS: Array<{
  key: string;
  label: string;
  group: 'weight' | 'count';
}> = [
  { key: 'kg', label: 'Kilogramos (kg)', group: 'weight' },
  { key: 'g', label: 'Gramos (g)', group: 'weight' },
  { key: 'lb', label: 'Libras (lb)', group: 'weight' },
  { key: 'oz', label: 'Onzas (oz)', group: 'weight' },
  { key: 'pieza', label: 'Pieza / unidad', group: 'count' },
  { key: 'pack', label: 'Pack', group: 'count' },
  { key: 'pcs', label: 'Piezas (pcs)', group: 'count' },
];

export function convertToKg(
  value: number | null,
  unit: string | null,
  speciesId: number
): { weight_kg: number | null; weight_source: 'explicit' | 'estimated' | null } {
  if (value == null || unit == null)
    return { weight_kg: null, weight_source: null };
  const u = String(unit).toLowerCase().trim();
  if (u in UNIT_TO_KG) {
    return {
      weight_kg: +(value * UNIT_TO_KG[u]).toFixed(4),
      weight_source: 'explicit',
    };
  }
  if (COUNT_UNITS.has(u)) {
    const perPiece = PER_PIECE_KG_BY_SPECIES[speciesId];
    if (perPiece != null) {
      return {
        weight_kg: +(value * perPiece).toFixed(4),
        weight_source: 'estimated',
      };
    }
  }
  return { weight_kg: null, weight_source: null };
}

export function computeUsdPerKg(
  priceLocal: number | null,
  currency: string,
  weightKg: number | null,
  fxRate: number | null
): number | null {
  if (
    priceLocal == null ||
    !weightKg ||
    weightKg <= 0 ||
    !currency ||
    fxRate == null ||
    fxRate === 0
  ) {
    return null;
  }
  // fxRate is "currency per 1 USD" (e.g. 1 USD = 17.29 MXN), so
  // price in USD = priceLocal / fxRate.
  return +(priceLocal / fxRate / weightKg).toFixed(4);
}

// Units of currency per 1 USD. Used when Frankfurter (ECB) doesn't publish a
// rate — either the API is down, or the currency isn't in ECB's list at all
// (SAR/COP/PEN/RUB/CLP). SAR is USD-pegged (3.75), so exact; the volatile ones
// (COP/RUB) drift, but are fine for comparing nearby weeks. RON/SEK/CHF do come
// from Frankfurter; these are just a backup if the API fails.
const FX_FALLBACK: Record<string, number> = {
  USD: 1,
  GBP: 0.79,
  EUR: 0.92,
  NZD: 1.65,
  AUD: 1.5,
  BRL: 5.0,
  MXN: 18.0,
  INR: 83.0,
  JPY: 150.0,
  KRW: 1350.0,
  CNY: 7.2,
  HKD: 7.8,
  // Frankfurter-supported (backup values)
  RON: 4.57,
  SEK: 9.65,
  CHF: 0.8,
  // Not in Frankfurter — fallback is the only source. Values from a live
  // FX source (open.er-api.com, 2026-07). SAR is USD-pegged (exact); the
  // rest are approximate and drift over time.
  SAR: 3.75,
  PEN: 3.4,
  COP: 3390,
  RUB: 77.4,
  CLP: 925,
};

/**
 * Fetch the current FX rate (units of `currency` per 1 USD) from
 * Frankfurter — the same source the scraper edge function uses, so a
 * manual entry and an automated scrape on the same day land on the
 * same USD/kg. Falls back to a hardcoded table if the API is down.
 */
export async function fetchRetailFxRate(
  currency: string
): Promise<{ rate: number; isFallback: boolean }> {
  if (currency === 'USD') return { rate: 1, isFallback: false };
  try {
    // Use the .dev host directly — .app issues a 301 redirect to .dev
    // that the browser fetch can trip over on CORS. Same JSON shape.
    const res = await fetch(
      `https://api.frankfurter.dev/v1/latest?from=USD&to=${encodeURIComponent(
        currency
      )}`
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const rate = data?.rates?.[currency];
    if (rate == null) throw new Error('rate missing');
    return { rate, isFallback: false };
  } catch {
    return { rate: FX_FALLBACK[currency] ?? 1, isFallback: true };
  }
}
